'use client';

import { useEffect, useRef } from 'react';
import { useToast } from '@/lib/context/ToastContext';

interface SupabaseStorageErrorEvent extends CustomEvent {
  detail: {
    error: string;
    key: string;
  };
}

// 30 seconds - don't spam auth error messages
const DEBOUNCE_INTERVAL = 30 * 1000;

/**
 * Hook to monitor Supabase auth storage errors
 *
 * Listens for 'supabase-storage-error' events and displays
 * user-friendly error messages when auth operations fail due to
 * storage issues (quota exceeded, storage disabled, etc.)
 */
export function useSupabaseStorageMonitor() {
  const { error } = useToast();
  const lastErrorTimeRef = useRef<number>(0);

  useEffect(() => {
    const handleStorageError = (event: Event) => {
      const { detail: _detail } = event as SupabaseStorageErrorEvent;
      const now = Date.now();

      // Debounce: Don't show similar errors more than once per 30 seconds
      if (now - lastErrorTimeRef.current < DEBOUNCE_INTERVAL) {
        return;
      }

      lastErrorTimeRef.current = now;

      // Show user-friendly error message
      error(
        'Unable to complete authentication due to storage limitations. Please clear your browser data or try again in a private window.',
        { duration: 10000 } // 10 seconds
      );
    };

    window.addEventListener('supabase-storage-error', handleStorageError);

    return () => {
      window.removeEventListener('supabase-storage-error', handleStorageError);
    };
  }, [error]);
}
